import { api, type Page } from './client'

/**
 * F-066 (TASK-0320): client di `JobsController` (`/api/jobs`, solo Admin) e di `JobProgressController`
 * (`/api/jobs/mine/{id}`, qualsiasi utente autenticato ma solo per i job che ha lanciato lui).
 */
export type JobListStatus = 'pending' | 'failed' | 'cancelled' | 'processed'

export interface JobRow {
  id: number
  jobType: string
  status: string
  attempts: number
  lastError: string | null
  createdAt: string
  processedAt: string | null
  failedAt: string | null
  nextAttemptAt: string | null
  correlationId: string | null
  assignedToUserId: number | null
  cancelledAt: string | null
  supersededByJobId: number | null
  /** R3-19: pratica e operatore del job (dal payload), null se non lo riguardano. */
  contractId: number | null
  operatorId: number | null
}

export interface JobDetail {
  id: number
  jobType: string
  payload: string
  status: string
  attempts: number
  lastError: string | null
  createdAt: string
  processedAt: string | null
  failedAt: string | null
  nextAttemptAt: string | null
  correlationId: string | null
  idempotencyKey: string | null
  assignedToUserId: number | null
  assignedAt: string | null
  assignmentNote: string | null
  cancelledAt: string | null
  cancelledByUserId: number | null
  cancelReason: string | null
  supersededByJobId: number | null
  requestedByUserId: number | null
  claimed: boolean
  contractId: number | null
  operatorId: number | null
}

export interface JobHealth {
  pending: number
  deadLetterCount: number
  cancelledCount: number
  oldestPendingAgeSeconds: number | null
  processedLast24h: number
  failedLast24h: number
  jobFailureRatePercent: number | null
  outboxDeadLetterCount: number
  combinedDeadLetterCount: number
}

/** Stato di un job lanciato dall'utente corrente (`GET /api/jobs/mine/{id}`). */
export interface MyJobState {
  id: number
  status: 'pending' | 'processed' | 'failed' | 'cancelled'
  finished: boolean
  attempts: number
  error: string | null
  total: number | null
  done: number | null
  /** Risultato del job (oggetto JSON deciso dal job stesso), presente solo a lavoro concluso. */
  result: Record<string, unknown> | null
  /** R3-19: il file prodotto dal job (export asincrono), scaricabile da chi lo ha lanciato. */
  file: { name: string; contentType: string; size: number; url: string } | null
  createdAt: string
  finishedAt: string | null
}

export const jobsApi = {
  health: () => api<JobHealth>('/api/jobs/health'),

  list: (status: JobListStatus, page = 1, size = 25, filters: { contractId?: number; operatorId?: number } = {}) =>
    api<Page<JobRow> & { status: string }>(
      `/api/jobs?status=${status}&page=${page}&size=${size}`
        + (filters.contractId ? `&contractId=${filters.contractId}` : '')
        + (filters.operatorId ? `&operatorId=${filters.operatorId}` : '')),

  detail: (id: number) => api<JobDetail>(`/api/jobs/${id}`),

  /** 204; 404 se non e' piu' Failed; 409 se la chiave di idempotenza e' gia' occupata da un job in coda. */
  requeue: (id: number) => api<void>(`/api/jobs/${id}/requeue`, { method: 'POST' }),

  /** 204; 409 se un worker lo ha gia' preso in carico o se e' gia' concluso. */
  cancel: (id: number, reason: string) =>
    api<void>(`/api/jobs/${id}/cancel`, { method: 'POST', body: JSON.stringify({ reason }) }),

  /** `userId` null = toglie l'assegnazione. */
  assign: (id: number, userId: number | null, note: string) =>
    api<void>(`/api/jobs/${id}/assign`, { method: 'POST', body: JSON.stringify({ userId, note }) }),

  /**
   * Solo per un job Failed. `payload` e' l'oggetto JSON gia' interpretato: 400 se non e' valido per il tipo
   * del job. Risponde con l'id del nuovo job in coda; l'originale diventa Annullato.
   */
  correct: (id: number, payload: unknown) =>
    api<{ newJobId: number }>(`/api/jobs/${id}/correct`, { method: 'POST', body: JSON.stringify({ payload }) }),

  /** 404 se il job non esiste o non e' stato lanciato da chi chiama. */
  mine: (id: number) => api<MyJobState>(`/api/jobs/mine/${id}`),
}
